import {
    Autocomplete,
    Card,
    Dialog,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { ExperimentMissionModal, TraitGroupModal } from ".";
import { Link, useSearchParams } from "react-router-dom";
import { PAGINATION, PIPELINE_INSTANCE_STATUS } from "../../constants";
import {
    createSortSearchParams,
    getGridOperators,
    objectArrayStringify,
    userNamesComparator,
} from "../utils";
import { formatDateTime, offset } from "../../utils/formatTime";
import { useCallback, useMemo, useState } from "react";
import { useFetch, useSnackbar, useUsers } from "../../hooks";

import { AlignmentModal } from "../alignment-report";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { BACKEND_ROUTES } from "../../backendRoutes";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import EditIcon from "@mui/icons-material/Edit";
import { FRONTEND_ROUTES } from "../../frontendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import InfoIcon from "@mui/icons-material/Info";
import LinkIcon from "@mui/icons-material/Link";
import { PipelineInstanceStatusPieChart } from "./PipelineInstanceStatusPieChart";
import PropTypes from "prop-types";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import Scrollbar from "../../components/Scrollbar";
import { TrafficTableSkeleton } from "./TrafficTableSkeleton";
import { laggy } from "../../utils/SWRLaggy";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

// `filter` prop not ideal but temporary. It should be removed in https://hiphen.atlassian.net/browse/SE-827
export const TrafficTable = ({ statusFilterString }) => {
    const theme = useTheme();
    const { patch } = useFetch();
    const { openSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedExperimentMissionUuid = searchParams.get(
        selectedExperimentMissionQueryString
    );
    const [selectedMission, setSelectedMission] = useState(null);
    const [isTraitGroupModalOpen, setIsTraitGroupModalOpen] = useState(
        Boolean(selectedExperimentMissionUuid)
    );
    const [isAlignmentModalOpen, setIsAlignmentModalOpen] = useState(false);
    const [isExperimentMissionModalOpen, setIsExperimentMissionModalOpen] =
        useState(false);

    const [{ sortModel, paginationModel }, setGridState] = useState({
        ...initialState.sorting,
        ...initialState.pagination,
    });

    const filterParams = {
        contractUuid: searchParams.get("contractUuid"),
        companyUuid: searchParams.get("companyUuid"),
        siteUuid: searchParams.get("siteUuid"),
        missionUuid: searchParams.get("missionUuid"),
        processingLeadUuid: searchParams.get("processingLeadUuid"),
        pipelineInstanceStatus: searchParams.get("pipelineInstanceStatus"),
    };

    const experimentMissionSearchParams = new URLSearchParams({
        parentInfo: true,
        pipelineInstanceStatusCounts: true,
        limit: pageItemCount,
        offset: paginationModel.page * pageItemCount,
        ...(filterParams.companyUuid && {
            Mission_Site_Contract_companyUuid: filterParams.companyUuid,
        }),
        ...(filterParams.contractUuid && {
            Mission_Site_contractUuid: filterParams.contractUuid,
        }),
        ...(filterParams.siteUuid && {
            Mission_siteUuid: filterParams.siteUuid,
        }),
        ...(filterParams.missionUuid && {
            missionUuid: filterParams.missionUuid,
        }),
        ...(filterParams.pipelineInstanceStatus && {
            pipelineInstanceStatus: filterParams.pipelineInstanceStatus,
        }),
        ...createSortSearchParams(sortModel, sortMap),
    });

    const {
        data: experimentMissions,
        error: experimentMissionsFetchError,
        mutate,
        isLagging,
    } = useSWR(
        `${BACKEND_ROUTES.EXPERIMENT_MISSION}?${statusFilterString}&${experimentMissionSearchParams}`,
        { use: [laggy] }
    );

    const { data: companies, error: companiesFetchError } = useSWRImmutable(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );

    const { data: contracts, error: contractsFetchError } = useSWRImmutable(
        `${BACKEND_ROUTES.CONTRACT}?sort=name${filterParams.companyUuid ? `&companyUuid=${filterParams.companyUuid}` : ""}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
    );

    const { data: sites, error: sitesFetchError } = useSWRImmutable(
        filterParams.contractUuid
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${filterParams.contractUuid}`
            : null
    );

    const { data: missions, error: missionsFetchError } = useSWRImmutable(
        filterParams.siteUuid
            ? `${BACKEND_ROUTES.MISSION}?sort=date&siteUuid=${filterParams.siteUuid}`
            : null
    );

    const { data: users, error: usersFetchError } = useUsers();

    const handleProcessRowUpdateError = useCallback(
        (error) => openSnackbar(error.message, "error"),
        [openSnackbar]
    );

    const updatePagination = useCallback(
        (newPaginationModel) =>
            setGridState((prevState) => ({
                ...prevState,
                paginationModel: newPaginationModel,
            })),
        []
    );

    const updateSort = useCallback(
        (newSortModel) =>
            setGridState((prevState) => ({
                ...prevState,
                sortModel: newSortModel,
            })),
        []
    );

    const processRowUpdate = useCallback(
        async (newRow) => {
            const updateMission = patch(
                `${BACKEND_ROUTES.MISSION}/${newRow.missionUuid}`,
                {
                    body: {
                        processingLeadUuid:
                            newRow.processingLeadUuid === invalidUuid
                                ? null
                                : newRow.processingLeadUuid,
                    },
                    forwardError: true,
                }
            );
            await mutate(updateMission, { populateCache: false });

            openSnackbar(
                `Updated Mission of ${formatDateTime(
                    newRow.acquisitionDate
                )} successfully.`,
                "success"
            );

            return newRow;
        },
        [mutate, openSnackbar, patch]
    );

    const selectedItems = useMemo(() => {
        return {
            company: companies
                ? companies.rows.find(
                      (company) => company.uuid === filterParams.companyUuid
                  )
                : null,
            contract: contracts
                ? contracts.rows.find(
                      (contract) => contract.uuid === filterParams.contractUuid
                  )
                : null,
            site: sites
                ? sites.rows.find((site) => site.uuid === filterParams.siteUuid)
                : null,
            mission: missions
                ? missions.rows.find(
                      (mission) => mission.uuid === filterParams.missionUuid
                  )
                : null,
        };
    }, [
        companies,
        contracts,
        sites,
        missions,
        filterParams.companyUuid,
        filterParams.contractUuid,
        filterParams.siteUuid,
        filterParams.missionUuid,
    ]);

    const rows = useMemo(
        () =>
            experimentMissions?.rows.map((experimentMission) => ({
                id: experimentMission.uuid,
                uuid: experimentMission.uuid,
                experiment: experimentMission.Experiment.name,
                site: experimentMission.Experiment.Site.name,
                pipelineTemplate: experimentMission.PipelineTemplate.name,
                bbchStage: experimentMission.BBCHStage.stage,
                dataType: objectArrayStringify(
                    experimentMission.Mission.AcquisitionVector.SensorBundles,
                    ["Sensor", "dataType"]
                ),
                acquisitionDate: new Date(experimentMission.Mission.date),
                missionUuid: experimentMission.missionUuid,
                experimentUuid: experimentMission.experimentUuid,
                processingLeadUuid:
                    experimentMission.Mission.processingLeadUuid ?? invalidUuid,
                campaign: experimentMission.Experiment.Site.Contract.name,
                contractUuid: experimentMission.Experiment.Site.contractUuid,
                company:
                    experimentMission.Experiment.Site.Contract.Company.name,
                processingStatus: experimentMission.processingStatus,
                pipelineInstanceStatusCounts:
                    experimentMission.pipelineInstanceStatusCounts,
            })),
        [experimentMissions]
    );

    // DataGrid column definitions
    const columns = useMemo(
        () =>
            users && [
                {
                    field: "pipelineInstanceStatusCounts",
                    headerName: "Processing",
                    minWidth: 80,
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        const counts = params.value;
                        if (!counts) return null;

                        const data = [
                            {
                                label: PIPELINE_INSTANCE_STATUS.PLANNED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.PLANNED] ??
                                    0,
                                color: theme.palette.grey[400],
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ONGOING,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ONGOING] ??
                                    0,
                                color: theme.palette.info.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.SUSPENDED,
                                value:
                                    counts[
                                        PIPELINE_INSTANCE_STATUS.SUSPENDED
                                    ] ?? 0,
                                color: theme.palette.warning.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.FINISHED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.FINISHED] ??
                                    0,
                                color: theme.palette.success.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ERROR,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ERROR] ?? 0,
                                color: theme.palette.error.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ABORTED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ABORTED] ??
                                    0,
                                color: theme.palette.error.dark,
                            },
                        ];

                        const total = data.reduce(
                            (sum, item) => sum + item.value,
                            0
                        );

                        const openValidationTraitModal = () => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                newSearchParams.set(
                                    selectedExperimentMissionQueryString,
                                    params.row.uuid
                                );
                                return newSearchParams;
                            });
                            setIsTraitGroupModalOpen(true);
                        };

                        if (total === 0)
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        width: "100%",
                                    }}
                                >
                                    <Tooltip
                                        title="No processing"
                                        placement="right"
                                    >
                                        <IconButton
                                            sx={{
                                                fontSize: 40,
                                                lineHeight: 1,
                                            }}
                                            onClick={openValidationTraitModal}
                                        >
                                            &#x2300;
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            );

                        return (
                            <div
                                onClick={openValidationTraitModal}
                                style={{ cursor: "pointer" }}
                            >
                                <PipelineInstanceStatusPieChart
                                    pipelineInstancesCountPerStatus={data}
                                />
                            </div>
                        );
                    },
                },
                {
                    field: "actions",
                    headerName: "Actions",
                    type: "actions",
                    minWidth: 130,
                    flex: 2,
                    filterable: false,
                    getActions: (params) => {
                        const openAlignmentModal = () => {
                            setSelectedMission(params.row.missionUuid);
                            setIsAlignmentModalOpen(true);
                        };

                        const openExperimentMissionModal = () => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                newSearchParams.set(
                                    selectedExperimentMissionQueryString,
                                    params.row.uuid
                                );
                                return newSearchParams;
                            });
                            setIsExperimentMissionModalOpen(true);
                        };

                        return [
                            <Tooltip title="Edit Experiment Mission">
                                <GridActionsCellItem
                                    label="Edit Experiment Mission"
                                    icon={<EditIcon />}
                                    onClick={openExperimentMissionModal}
                                />
                            </Tooltip>,
                            <GridActionsCellItem
                                label="Acquisition report"
                                icon={<QueryStatsIcon />}
                                component={Link}
                                to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC}/${FRONTEND_ROUTES.ACQUISITION_REPORT}/${params.row.missionUuid}?experimentMissionUuid=${params.row.id}`}
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label="Alignment report"
                                icon={<CropOriginalIcon />}
                                onClick={openAlignmentModal}
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label="Go to Association"
                                icon={<LinkIcon />}
                                component={Link}
                                to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC_DETAILS}/${params.row.missionUuid}`}
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label="Go to Campaign"
                                icon={<ArticleOutlinedIcon />}
                                component={Link}
                                to={`/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${params.row.contractUuid}`}
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label="Copy mission uuid"
                                icon={<ContentCopyIcon />}
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        params.row.missionUuid
                                    )
                                }
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label="Copy experiment uuid"
                                icon={<ContentCopyIcon />}
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        params.row.experimentUuid
                                    )
                                }
                                showInMenu
                            />,
                        ];
                    },
                },
                {
                    field: "company",
                    headerName: "Company",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "campaign",
                    headerName: "Campaign",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "site",
                    headerName: "Site",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "acquisitionDate",
                    type: "date",
                    headerName: "Acquisition Date",
                    minWidth: 140,
                    flex: 2,
                    filterable: false,
                },
                {
                    field: "experiment",
                    headerName: "Experiment",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "dataType",
                    headerName: "Type",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "pipelineTemplate",
                    headerName: "Pipeline Template",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "bbchStage",
                    headerName: "BBCH",
                    type: "number",
                    minWidth: 65,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "processingLeadUuid",
                    renderHeader: () => (
                        <div className="MuiDataGrid-columnHeaderTitle">
                            {"Processing Lead"}
                            <Tooltip
                                title={
                                    <Typography>
                                        Warning: Changing the mission processing
                                        lead of one row affects all the other
                                        rows of the same mission.
                                    </Typography>
                                }
                            >
                                <InfoIcon fontSize="small" color="warning" />
                            </Tooltip>
                        </div>
                    ),
                    minWidth: 160,
                    flex: 2,
                    type: "singleSelect",
                    filterOperators: getGridOperators("select"),
                    editable: true,
                    sortComparator: (userUuidA, userUuidB) =>
                        userNamesComparator(
                            userUuidA,
                            userUuidB,
                            users,
                            invalidUuid,
                            "No Processing Lead"
                        ),
                    valueOptions: [
                        ...users.map((user) => ({
                            value: user.uuid,
                            label: `${user.firstName} ${user.lastName}`,
                        })),
                        { value: invalidUuid, label: "No processing lead" },
                    ],
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "processingStatus",
                    headerName: "Status",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
            ],
        [setSearchParams, theme.palette, users]
    );

    const mergedFetchError =
        usersFetchError ??
        experimentMissionsFetchError ??
        missionsFetchError ??
        contractsFetchError ??
        sitesFetchError ??
        companiesFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!companies || !experimentMissions || !users) {
        return <TrafficTableSkeleton />;
    }

    return (
        <Stack justifyContent="space-between" mb={2} spacing={1}>
            <Grid container spacing={1}>
                <Grid size={3}>
                    <Autocomplete
                        id="company"
                        value={selectedItems.company ?? null}
                        options={companies?.rows ?? []}
                        isOptionEqualToValue={(option, value) =>
                            option.uuid === value.uuid
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(_, value) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (value) {
                                    if (
                                        selectedItems.contract?.companyUuid !==
                                        value.uuid
                                    ) {
                                        newSearchParams.delete("missionUuid");
                                        newSearchParams.delete("siteUuid");
                                        newSearchParams.delete("contractUuid");
                                    }
                                    newSearchParams.set(
                                        "companyUuid",
                                        value.uuid
                                    );
                                } else {
                                    newSearchParams.delete("companyUuid");
                                }
                                return newSearchParams;
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter on company" />
                        )}
                    />
                </Grid>
                <Grid size={3}>
                    <Autocomplete
                        id="contract"
                        value={selectedItems.contract ?? null}
                        options={contracts?.rows ?? []}
                        isOptionEqualToValue={(option, value) =>
                            option.uuid === value.uuid
                        }
                        getOptionLabel={(option) => option.name}
                        getOptionKey={(option) => option.uuid}
                        onChange={(_, value) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (value) {
                                    newSearchParams.delete("missionUuid");
                                    newSearchParams.delete("siteUuid");
                                    newSearchParams.set(
                                        "contractUuid",
                                        value.uuid
                                    );
                                } else {
                                    newSearchParams.delete("missionUuid");
                                    newSearchParams.delete("siteUuid");
                                    newSearchParams.delete("contractUuid");
                                }
                                return newSearchParams;
                            });
                        }}
                        disabled={!contracts}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter on campaign" />
                        )}
                    />
                </Grid>
                <Grid size={3}>
                    <Autocomplete
                        id="site"
                        options={sites?.rows ?? []}
                        value={selectedItems.site ?? null}
                        isOptionEqualToValue={(option, value) =>
                            option.uuid === value.uuid
                        }
                        getOptionLabel={(option) => option.name}
                        getOptionKey={(option) => option.uuid}
                        onChange={(_, value) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (value) {
                                    newSearchParams.delete("missionUuid");
                                    newSearchParams.set("siteUuid", value.uuid);
                                } else {
                                    newSearchParams.delete("missionUuid");
                                    newSearchParams.delete("siteUuid");
                                }
                                return newSearchParams;
                            });
                        }}
                        disabled={!filterParams.contractUuid || !sites}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter on site" />
                        )}
                    />
                </Grid>
                <Grid size={3}>
                    <Autocomplete
                        id="mission"
                        options={missions?.rows ?? []}
                        value={selectedItems.mission ?? null}
                        isOptionEqualToValue={(option, value) =>
                            option.uuid === value.uuid
                        }
                        getOptionLabel={(option) =>
                            formatDateTime(offset(new Date(option.date), true))
                        }
                        getOptionKey={(option) => option.uuid}
                        onChange={(_, value) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (value) {
                                    newSearchParams.set(
                                        "missionUuid",
                                        value.uuid
                                    );
                                } else {
                                    newSearchParams.delete("missionUuid");
                                }
                                return newSearchParams;
                            });
                        }}
                        disabled={!filterParams.siteUuid || !missions}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter on mission" />
                        )}
                    />
                </Grid>
                <Grid size={3}>
                    <Autocomplete
                        id="pipelineInstanceStatus"
                        value={filterParams.pipelineInstanceStatus ?? null}
                        options={Object.values(PIPELINE_INSTANCE_STATUS)}
                        onChange={(_, value) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (value) {
                                    newSearchParams.set(
                                        "pipelineInstanceStatus",
                                        value
                                    );
                                } else {
                                    newSearchParams.delete(
                                        "pipelineInstanceStatus"
                                    );
                                }
                                return newSearchParams;
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter on processing status"
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Card>
                <Scrollbar>
                    <DataGrid
                        rowHeight={65}
                        rows={rows}
                        columns={columns}
                        pageSizeOptions={pageSizeOptions}
                        initialState={initialState}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={updatePagination}
                        // count is incorrect as data is filtered in frontend. Will be fixed in https://hiphen.atlassian.net/browse/SE-827
                        rowCount={experimentMissions?.count}
                        sortingMode="server"
                        onSortModelChange={updateSort}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        loading={isLagging}
                    />
                </Scrollbar>
            </Card>
            <Dialog
                open={isTraitGroupModalOpen}
                fullWidth
                maxWidth="lg"
                onTransitionExited={() => {
                    setSearchParams((prevState) => {
                        const newSearchParams = new URLSearchParams(prevState);
                        newSearchParams.delete(
                            selectedExperimentMissionQueryString
                        );
                        return newSearchParams;
                    });
                }}
            >
                <TraitGroupModal
                    experimentMissionUuid={selectedExperimentMissionUuid}
                    closeModal={() => setIsTraitGroupModalOpen(false)}
                />
            </Dialog>
            <Dialog open={isAlignmentModalOpen} fullWidth maxWidth="xl">
                <AlignmentModal
                    missionUuid={selectedMission}
                    onClose={() => setIsAlignmentModalOpen(false)}
                />
            </Dialog>
            <Dialog
                open={isExperimentMissionModalOpen}
                fullWidth
                maxWidth="sm"
                onTransitionExited={() => {
                    setSearchParams((prevState) => {
                        const newSearchParams = new URLSearchParams(prevState);
                        newSearchParams.delete(
                            selectedExperimentMissionQueryString
                        );
                        return newSearchParams;
                    });
                }}
            >
                <ExperimentMissionModal
                    experimentMissionUuid={selectedExperimentMissionUuid}
                    closeModal={() => setIsExperimentMissionModalOpen(false)}
                    mutateTable={mutate}
                />
            </Dialog>
        </Stack>
    );
};

const pageItemCount = 50;
const pageSizeOptions = [pageItemCount];

const invalidUuid = "invalid uuid";

const initialState = {
    sorting: { sortModel: [{ field: "acquisitionDate", sort: "desc" }] },
    columns: {
        columnVisibilityModel: {
            pipelineTemplate: false,
            bbchStage: false,
            processingLeadUuid: false,
        },
    },
    pagination: { paginationModel: { page: 0, pageSize: pageSizeOptions[0] } },
};

const sortMap = { acquisitionDate: "Mission_date" };

const selectedExperimentMissionQueryString = "selectedExperimentMission";

TrafficTable.propTypes = {
    statusFilterString: PropTypes.string.isRequired,
};
